import { useRef, useEffect, useReducer } from "react";
import PropTypes from "prop-types";
import "./styles/themes/_default.scss";
import Option, { ActiveOption } from "./components/Option/component";
import * as utils from "./utils";
import Icon from "../Icon/index";
import classNames from "classnames";
import SelectedStateLabel from "./components/SelectedStateLabel/component";
import AccessibilityMarkup from "./components/Accessibility/component";

const DropDown = ({
    options = [],
    defaultSelection = "Select",
    setSelectedOption = () => {},
    accessibilityLabelString = "Select an option",
    accessibilityKey,
    className,
}) => {
    const inputRef = useRef();
    const listRef = useRef();
    const [state, dispatch] = useReducer(utils.reducer, {
        options,
        active: false,
        searchText: "",
        display: [],
        displayCount: 0,
        activeIndex: -1,
    });
    const { active, searchText, displayCount, display, activeIndex } = state;

    useEffect(() => {
        if (activeIndex === -1 && !active) {
            if (inputRef.current && searchText) {
                inputRef.current.focus();
            }
            setSelectedOption(searchText);
        }
    }, [searchText, activeIndex, active]);

    useEffect(() => {
        dispatch({ options });
    }, [options]);

    const externalFocusHandler = (e) => {
        if (
            e.target !== inputRef.current &&
            !utils.isDescendent(listRef.current, e.target)
        ) {
            dispatch({ type: utils.DESELECT });
        }
    };

    useEffect(() => {
        window.addEventListener("click", externalFocusHandler);
        window.addEventListener("focus", externalFocusHandler);
        return () => {
            window.removeEventListener("click", externalFocusHandler);
            window.removeEventListener("focus", externalFocusHandler);
        };
    }, []);

    const renderOptions = display.map((text, index) => {
        const active = index === activeIndex;
        const key = `${text}-${active}-${index}-${accessibilityKey}`;
        const onClick = utils.optionOnClick(dispatch, index);
        const props = {
            key,
            hidden: false,
            onClick,
            role: "option",
            id: utils.getOptionAccesibilityId(accessibilityKey, index),
        };
        return active ? (
            <ActiveOption {...props}>{text}</ActiveOption>
        ) : (
            <Option {...props}>{text}</Option>
        );
    });

    const textInputClassName = classNames("dropDownTextInput", {
        active: active,
    });

    const optionListClassName = classNames(
        "optionListSelect",
        { active: active },
        { hidden: !active }
    );

    const dropDownClassName = classNames("dropDown", className, {
        active: active,
    });

    const chevronClassname = classNames("vertical", { open: active });
    const accessibilityListKey = `${accessibilityKey}-listbox`;

    return (
        //eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/interactive-supports-focus, jsx-a11y/role-has-required-aria-props, jsx-a11y/autocomplete-valid
        <div
            className={dropDownClassName}
            onKeyDown={utils.onKeyDown(dispatch)}
            //eslint-disable-next-line jsx-a11y/role-has-required-aria-props
            role="combobox"
            aria-expanded={active}
            aria-haspopup="listbox"
            aria-owns={accessibilityListKey}
            data-testid="dropdown"
        >
            <AccessibilityMarkup
                accessibilityLabelString={accessibilityLabelString}
                numOptions={displayCount}
                totalNumOptions={options.length}
            />
            <SelectedStateLabel active={!!searchText} text={"Sport"} />
            <input
                ref={inputRef}
                type="text"
                id="option-select-input"
                placeholder={defaultSelection}
                autoComplete="off"
                aria-controls={accessibilityListKey}
                aria-autocomplete="both"
                className={textInputClassName}
                value={searchText}
                onClick={utils.textInputOnClick(dispatch)}
                onChange={utils.textInputOnChange(dispatch)}
                aria-describedby="option-select-info"
                aria-activedescendant={utils.getOptionAccesibilityId(
                    accessibilityKey,
                    activeIndex
                )}
            ></input>
            <Icon type="triangle" className={chevronClassname} />
            <ul
                id={accessibilityListKey}
                role="listbox"
                className={optionListClassName}
                ref={listRef}
            >
                {renderOptions}
            </ul>
        </div>
    );
};

export default DropDown;

DropDown.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape({
            mainKey: PropTypes.string,
            secondaryKey: PropTypes.string,
            thirdKey: PropTypes.string,
        })
    ),
    setSelectedOption: PropTypes.func,
    defaultSelection: PropTypes.string,
    accessibilityLabelString: PropTypes.string,
    accessibilityKey: PropTypes.string.isRequired,
};
